// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  TEMPORARY — TESTING TOOL. Delete this file, the fenced block in          ║
// ║  pages/OrgAdminClients.jsx that renders it, and the backend's             ║
// ║  controllers/clientPurgeController.js + its route block, together.        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// Permanently deletes clients that came in through an import, so the Tally
// connector can be run, inspected, thrown away and run again. Nothing else in
// this application hard-deletes a client.
//
// ⚠️ THE PREVIEW IS NOT A COURTESY, IT IS THE SAFETY MECHANISM. The server
// refuses to delete without `confirm: true`, and the same scan produces both
// answers — so what this screen lists is exactly what the second call removes.
// The user must see the list before the button that applies it appears.
import { useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Modal, useToast } from '../../../design-system';
import { SOURCE_LABELS } from '../../../lib/clientOrigin';

function PurgeImportedModal({ isOpen, onClose, source, batchId = '', onPurged }) {
  const toast = useToast();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const close = () => {
    setPreview(null);
    setResult(null);
    setError('');
    onClose();
  };

  const call = async (confirm) => {
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.post('/api/clients/purge', { source, batchId, confirm });
      if (confirm) {
        setResult(res.data);
        toast.success(`Permanently deleted ${res.data.purgedCount} client(s)`);
        onPurged?.();
      } else {
        setPreview(res.data);
      }
    } catch (err) {
      // ⚠️ "That failed" IS NOT AN ERROR MESSAGE. The first version showed
      // exactly that and nobody — including the person who wrote it — could
      // tell a missing endpoint from a permission problem from a timeout.
      // The status is the most useful single fact, so it always shows.
      const status = err.response?.status;
      const said = err.response?.data?.error;
      let hint = '';
      if (!err.response) hint = ' The server did not answer — it may still be working, or the request timed out.';
      else if (status === 404) hint = ' This endpoint is missing, so the server has not been restarted with it yet.';
      else if (status === 403) hint = ' This needs an Admin account.';
      else if (status >= 500) hint = ' The server errored — its log will name the cause.';
      setError(`${said || 'The request failed'}${status ? ` (HTTP ${status})` : ''}.${hint}`);
    } finally {
      setBusy(false);
    }
  };

  const label = SOURCE_LABELS[source] || source;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Delete imported clients"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && !preview && (
            <Button variant="primary" onClick={() => call(false)} disabled={busy}>
              {busy ? 'Checking…' : 'Show me what would be deleted'}
            </Button>
          )}
          {/* ⚠️ The destructive action is unreachable until the preview has been
              fetched and has something in it — there is no path from opening
              this dialog straight to a deletion. */}
          {!result && preview && preview.wouldPurge > 0 && (
            <Button variant="danger" onClick={() => call(true)} disabled={busy}>
              {busy ? 'Deleting…' : `Permanently delete ${preview.wouldPurge}`}
            </Button>
          )}
        </>
      }
    >
      <div className="purge-modal">
        <p className="purge-scope">
          Scope: <strong>{label}</strong>
          {batchId ? <> · batch <code>{batchId}</code></> : null}
        </p>

        {/* ⚠️ THE ORIGINAL COPY READ AS THOUGH THIS DELETED FROM TALLY. It said
            it "removes the ledgers and vouchers synced under it" without saying
            WHERE, and the only reasonable reading of that, standing in front of
            a firm's live books, is the worst one. Nothing here can reach Tally:
            the tool makes no network call and creates no agent job, and only an
            agent claiming a job can talk to Tally at all. */}
        <p className="purge-warning">
          ⚠️ This removes the client records from <strong>this software</strong> completely —
          not &ldquo;closed&rdquo;, gone. It also clears the copy of their ledgers and vouchers
          held here, and unlinks the Tally connection, so the import can be run again from
          scratch. This cannot be undone.
        </p>
        <p className="purge-safe">
          ✅ <strong>Your Tally data is not touched.</strong> Nothing is deleted, moved or
          written in Tally or on the Tally drive — only this software&rsquo;s own records.
        </p>
        <p className="purge-note">
          Clients entered by hand are never touched, and neither is any client that already has
          work against it — a task, an invoice, a filing, a document, a portal login or a chat.
        </p>

        {error && <p className="purge-error">{error}</p>}

        {preview && !result && (
          <>
            <h4>
              {preview.wouldPurge} to delete
              {preview.wouldKeep > 0 ? `, ${preview.wouldKeep} kept` : ''}
            </h4>
            {preview.wouldPurge === 0 && <p>Nothing matches — there is nothing to delete.</p>}
            {preview.purgeable.length > 0 && (
              <ul className="purge-list">
                {preview.purgeable.map((c) => (
                  <li key={c.clientId}>
                    <strong>{c.displayName}</strong>
                    {c.clientCode ? ` (${c.clientCode})` : ''}
                    {c.detail ? <span className="purge-detail"> — from {c.detail}</span> : null}
                  </li>
                ))}
              </ul>
            )}
            {/* Why a row survived matters more than that it did: it is the
                signal that somebody has started treating a test record as real. */}
            {preview.kept.length > 0 && (
              <>
                <h4>Kept — already in use</h4>
                <ul className="purge-list purge-kept">
                  {preview.kept.map((c) => (
                    <li key={c.clientId}>
                      <strong>{c.displayName}</strong>
                      <span className="purge-detail"> — {c.reason}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        {result && (
          <>
            <h4>Deleted {result.purgedCount} client(s)</h4>
            <ul className="purge-list">
              {Object.entries(result.removed).map(([k, v]) => (
                <li key={k}>
                  {v} {k.replace(/([A-Z])/g, ' $1').toLowerCase()}
                </li>
              ))}
            </ul>
            {result.keptCount > 0 && <p>{result.keptCount} kept because they are in use.</p>}
            {result.failedCount > 0 && (
              <p className="purge-error">{result.failedCount} could not be deleted.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default PurgeImportedModal;
